import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import StandardizedAddressInput from "components/standardizedAddressInput";
import { VendorContext } from "contexts/VendorContext";
import type { Vendor } from "types/vendor";
import { useCompanyFeature } from "hooks/useCompanyFeature";
import VendorIntegrationPanel, { VendorIntegrationType } from "./VendorIntegrationPanel";
import {
  US_STATES,
  VENDOR_FORM_INITIAL,
  VendorFormData,
} from "./vendorFormTypes";
import {
  buildAddressObjFromVendor,
  getIntegrationTypeFromVendor,
  resetFormState,
  saveVendorHeritageBranch,
  syncVendorIntegration,
  vendorToFormData,
} from "./vendorFormUtils";

type Props = {
  mode: "create" | "edit";
  isOpen: boolean;
  onClose: () => void;
  refetch?: () => void | Promise<unknown>;
  vendor?: Vendor | null;
  focusHeritageCredentials?: boolean;
  onHeritageCredentialsFocusHandled?: () => void;
  /** Forwarded to VendorIntegrationPanel — post-download navigation to the link page (#31). */
  onPurchaseHistoryDownloaded?: () => void;
};

const VendorFormModal = ({
  mode,
  isOpen,
  onClose,
  refetch,
  vendor,
  focusHeritageCredentials = false,
  onHeritageCredentialsFocusHandled,
  onPurchaseHistoryDownloaded,
}: Props) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorsToolBar.VendorsToolBar";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { currentVendor, updater, handleUpdateVendor } = useContext(VendorContext);
  const heritageFeatureEnabled = useCompanyFeature("heritage");
  const queryClient = useQueryClient();

  const [vendorData, setVendorData] = useState<VendorFormData>(VENDOR_FORM_INITIAL);
  const [addressObj, setAddressObj] = useState(resetFormState().addressObj);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [initialIntegration, setInitialIntegration] = useState<VendorIntegrationType>("");
  const [pendingHeritageBranchId, setPendingHeritageBranchId] = useState<number | null>(null);

  const editVendor = mode === "edit" ? vendor ?? currentVendor : null;
  const vendorId = editVendor?.vendor_id ?? null;

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "edit" && editVendor) {
      setVendorData(vendorToFormData(editVendor));
      setAddressObj(buildAddressObjFromVendor(editVendor));
      setInitialIntegration(getIntegrationTypeFromVendor(editVendor));
    } else if (mode === "create") {
      const { vendorData: fresh, addressObj: freshAddr } = resetFormState();
      setVendorData(fresh);
      setAddressObj(freshAddr);
      setInitialIntegration("");
      setPendingHeritageBranchId(null);
    }
  }, [isOpen, mode, editVendor?.vendor_id]);

  useEffect(() => {
    if (vendorData.distributor_type_code !== "heritage") {
      setPendingHeritageBranchId(null);
    }
  }, [vendorData.distributor_type_code]);

  useEffect(() => {
    setVendorData((prev) => ({
      ...prev,
      city: addressObj.selectedOption.city,
      vendor_state: addressObj.selectedOption.state,
      zip_code: addressObj.selectedOption.zipcode,
    }));
  }, [addressObj.selectedOption.city, addressObj.selectedOption.state, addressObj.selectedOption.zipcode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setVendorData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    onClose();
    const { vendorData: fresh, addressObj: freshAddr } = resetFormState();
    setVendorData(fresh);
    setAddressObj(freshAddr);
    setPendingHeritageBranchId(null);
  };

  const createInputField = (key: keyof VendorFormData, placeholder: string, span?: string) => {
    const keyString = key as string;
    return (
      <div className={`${span ? span : "col-span-1"}`}>
        <label htmlFor={keyString} className="block text-sm font-medium text-gray-700">
          {placeholder === "Street Address" ? tr("Street Address") : placeholder}
        </label>
        {placeholder === "Street Address" ? (
          <StandardizedAddressInput {...{ setAddressObj, addressObj }} noLabel />
        ) : (
          <input
            type="text"
            name={keyString}
            id={keyString}
            value={vendorData[key]}
            onChange={handleInputChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        )}
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    const nextIntegration = (vendorData.distributor_type_code || "") as VendorIntegrationType;

    try {
      const body: Record<string, unknown> = {
        ...vendorData,
        street_address: addressObj.address,
        vendor_state: addressObj.selectedOption.state,
        city: addressObj.selectedOption.city,
        zip_code: addressObj.selectedOption.zipcode,
        isStandardizedAddress: addressObj.isStandardizedAddress,
      };
      if (!vendorData.distributor_type_code) {
        delete body.distributor_type_code;
      }

      if (mode === "create") {
        const res = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/addVendor`,
          body
        );
        const newVendorId: number | undefined =
          res.data?.vendor_id ?? res.data?.vendorId ?? res.data?.id;
        if (newVendorId != null && nextIntegration) {
          await syncVendorIntegration(newVendorId, "", nextIntegration);
          if (nextIntegration === "heritage" && pendingHeritageBranchId != null) {
            await saveVendorHeritageBranch(newVendorId, pendingHeritageBranchId);
          }
        }
      } else if (editVendor?.vendor_id != null) {
        const payload = {
          ...editVendor,
          ...vendorData,
          street_address: addressObj.address,
          vendor_state: addressObj.selectedOption.state,
          city: addressObj.selectedOption.city,
          zip_code: addressObj.selectedOption.zipcode,
          is_standardized_address: addressObj.isStandardizedAddress,
          is_heritage: nextIntegration === "heritage",
          is_siteone: nextIntegration === "siteone",
          distributor_type_code: nextIntegration || null,
        };
        await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/updateVendor`, {
          vendorId: editVendor.vendor_id,
          data: payload,
        });
        await syncVendorIntegration(editVendor.vendor_id, initialIntegration, nextIntegration);
        updater(payload);
        handleUpdateVendor(payload as Vendor);
        await queryClient.invalidateQueries(["vendors_items", editVendor.vendor_id]);
      }

      if (refetch) await refetch();
      handleCancel();
    } catch (error) {
      console.error("Error saving vendor:", error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : "Failed to save vendor. Please try again.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const title = mode === "create" ? tr("Add Vendor") : tr("Edit Vendor");
  const submitLabel = mode === "create" ? tr("Add") : tr("Save");

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4 sm:p-6"
      aria-labelledby="vendor-form-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={handleOverlayClick}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative flex flex-col w-full max-w-5xl max-h-[min(90vh,calc(100vh-2rem))] overflow-hidden rounded-lg bg-white text-left shadow-xl"
      >
            <div className="bg-white pb-4 pt-5 sm:p-6 sm:pb-4 overflow-y-auto flex-1">
              <div
                className="text-lg font-semibold leading-6 text-gray-900 mb-4"
                id="vendor-form-modal-title"
              >
                {title}
              </div>

              <div className="grid lg:grid-cols-2 lg:gap-10 md:gap-6 md:grid-cols-1 max-[640px]:px-3">
                <div className="grid grid-col-2 gap-6">
                  <div className="col-span-3">
                    <div className="text-base font-medium leading-6 text-gray-900">
                      {tr("Personal Information")}
                    </div>
                    <hr />
                    <div className="mt-4">
                      {createInputField("vendor_name", "Vendor Name")}
                      <div className="grid grid-cols-3 gap-6 mt-4">
                        <div className="col-span-1">
                          <label htmlFor="prefix" className="block text-sm font-medium text-gray-700">
                            {tr("Prefix")}
                          </label>
                          <select
                            id="prefix"
                            name="prefix"
                            value={vendorData.prefix}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">--</option>
                            <option value="Mr">Mr.</option>
                            <option value="Mrs">Mrs.</option>
                            <option value="Ms">Ms.</option>
                          </select>
                        </div>
                        {createInputField("first_name", "First Name")}
                        {createInputField("last_name", "Last Name")}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-3">
                    <h3 className="text-base font-medium leading-6 text-gray-900">
                      {tr("Contact Information")}
                    </h3>
                    <hr />
                    <div className="grid grid-cols-3 gap-6 mt-4">
                      <div className="col-span-1">
                        <label htmlFor="phone_type" className="block text-sm font-medium text-gray-700">
                          {tr("Phone Type")}
                        </label>
                        <select
                          id="phone_type"
                          name="phone_type"
                          value={vendorData.phone_type}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                        >
                          <option value="">--</option>
                          <option value="work">{tr("Work")}</option>
                          <option value="personal">{tr("Personal")}</option>
                          <option value="office">{tr("Office")}</option>
                        </select>
                      </div>
                      {createInputField("phone_number", "Phone Number")}
                      {createInputField("ext", "Extension")}
                      <div className="col-span-1">
                        <label htmlFor="email_type" className="block text-sm font-medium text-gray-700">
                          {tr("Email Type")}
                        </label>
                        <select
                          id="email_type"
                          name="email_type"
                          value={vendorData.email_type}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                        >
                          <option value="">--</option>
                          <option value="work">{tr("Work")}</option>
                          <option value="personal">{tr("Personal")}</option>
                        </select>
                      </div>
                      {createInputField("email_address", "Email Address", "col-span-2")}
                    </div>
                  </div>

                  <div className="col-span-3">
                    <div className="text-base font-medium leading-6 text-gray-900">
                      {tr("Address Information")}
                    </div>
                    <hr />
                    <div className="grid grid-cols-3 gap-6 mt-4">
                      {createInputField("street_address", "Street Address", "col-span-2")}
                      {createInputField("address_line_2", "Address Line 2")}
                      {createInputField("city", "City")}
                      <div className="col-span-1">
                        <label htmlFor="vendor_state" className="block text-sm font-medium text-gray-700">
                          {tr("State")}
                        </label>
                        <select
                          id="vendor_state"
                          name="vendor_state"
                          value={vendorData.vendor_state}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                        >
                          <option
                            value={
                              addressObj.selectedOption.state ? addressObj.selectedOption.state : ""
                            }
                          >
                            {addressObj.selectedOption.state ? addressObj.selectedOption.state : "--"}
                          </option>
                          {US_STATES.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                      {createInputField("zip_code", "Zip Code")}
                    </div>
                  </div>
                </div>

                <div className="grid grid-col-2 gap-6">
                  <div className="col-span-3">
                    <div className="text-base font-medium leading-6 text-gray-900">
                      Distributor integration
                    </div>
                    <hr />
                    <div className="grid grid-cols-1 gap-4 mt-2 max-w-md">
                      <label
                        htmlFor="distributor_type_code"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Integration
                      </label>
                      <select
                        id="distributor_type_code"
                        name="distributor_type_code"
                        value={vendorData.distributor_type_code}
                        onChange={handleInputChange}
                        className="mt-1 block w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">No Integration</option>
                        {(heritageFeatureEnabled ||
                          vendorData.distributor_type_code === "heritage") && (
                          <option value="heritage">Heritage</option>
                        )}
                        <option value="siteone">SiteOne</option>
                      </select>
                      <VendorIntegrationPanel
                        integrationType={(vendorData.distributor_type_code || "") as VendorIntegrationType}
                        vendorId={vendorId}
                        vendorName={vendorData.vendor_name?.trim() || editVendor?.vendor_name}
                        pendingHeritageBranchId={mode === "create" ? pendingHeritageBranchId : undefined}
                        onPendingHeritageBranchChange={
                          mode === "create" ? setPendingHeritageBranchId : undefined
                        }
                        showCatalogDownloads={mode === "edit"}
                        autoOpenHeritageCredentials={
                          isOpen && focusHeritageCredentials && vendorData.distributor_type_code === "heritage"
                        }
                        onHeritageCredentialsAutoOpenHandled={onHeritageCredentialsFocusHandled}
                        onPurchaseHistoryDownloaded={onPurchaseHistoryDownloaded}
                      />
                    </div>
                  </div>

                  <div className="col-span-3">
                    <div className="text-base font-medium leading-6 text-gray-900">
                      {tr("Additional Information")}
                    </div>
                    <hr />
                    <div className="grid grid-cols-2 gap-6 mt-4">
                      {createInputField("job_title", "Job Title")}
                      {createInputField("department", "Department")}
                      <div className="col-span-2">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                          {tr("Notes")}
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          value={vendorData.notes}
                          onChange={handleInputChange}
                          className="h-[80px] mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm resize-none"
                          placeholder={tr("Notes")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-4 sm:px-6 shrink-0 border-t border-gray-200">
              {submitError ? (
                <p className="text-sm text-red-600 mb-3 m-0" role="alert">
                  {submitError}
                </p>
              ) : null}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  onClick={handleCancel}
                  type="button"
                  className="inline-flex h-11 w-full sm:w-auto items-center justify-center rounded-md bg-white px-5 text-sm font-semibold leading-none text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 touch-manipulation"
                >
                  {tr("Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex h-11 w-full sm:w-auto items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold leading-none text-white shadow-sm hover:brightness-[0.9] disabled:opacity-60 touch-manipulation"
                >
                  {submitLabel}
                </button>
              </div>
            </div>
      </form>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default VendorFormModal;
